import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { timesheetsAPI, jobsAPI, resourcesAPI, clientsAPI, reportsAPI, usersAPI, clientInvoicesAPI, subcontractorInvoicesAPI } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import TimesheetAttachments from '../components/TimesheetAttachments';
import { formatDate, formatDateRange, formatDateShort } from '../utils/dateUtils';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  Send,
  Check,
  X,
  Loader2,
  AlertCircle,
  Download,
  Paperclip,
  CreditCard,
  DollarSign
} from 'lucide-react';

const StatusBadge = ({ status }) => {
  const statusConfig = {
    draft: { className: 'badge-draft', label: 'Draft' },
    submitted: { className: 'badge-pending', label: 'Pending Approval' },
    approved: { className: 'badge-approved', label: 'Approved' },
    rejected: { className: 'badge-rejected', label: 'Rejected' },
  };

  const config = statusConfig[status] || statusConfig.draft;
  return <span className={config.className}>{config.label}</span>;
};

const TimesheetDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const isNew = !id || id === 'new';

  const [timesheet, setTimesheet] = useState(null);
  const [timesheetOwner, setTimesheetOwner] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]); // All active jobs
  const [availableJobIds, setAvailableJobIds] = useState([]); // Jobs user has pay rates for
  const [resources, setResources] = useState([]);
  const [clients, setClients] = useState([]);
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [selectedJob, setSelectedJob] = useState('');
  const [entries, setEntries] = useState([]);
  const [comments, setComments] = useState('');
  const [rejectComment, setRejectComment] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedWeekStart, setSelectedWeekStart] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [linkedInvoices, setLinkedInvoices] = useState({ client: [], subcontractor: [] });
  const attachmentsRef = useRef(null);
  
  // Pay rate info for the current resource/job
  const [currentPayRate, setCurrentPayRate] = useState(null);
  
  // Admin creating timesheet on behalf of user
  const [subcontractors, setSubcontractors] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');

  // Generate past weeks for selection (back to Nov 23, 2025)
  const getPastWeeks = () => {
    const weeks = [];
    const today = new Date();
    
    // Find the start of current week (Sunday) in local timezone
    const currentSunday = new Date(today);
    const dayOfWeek = currentSunday.getDay();
    currentSunday.setDate(currentSunday.getDate() - dayOfWeek);
    currentSunday.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
    
    // Target date: Sunday, November 23, 2025
    const targetDate = new Date(2025, 10, 23, 12, 0, 0); // Month is 0-indexed, so 10 = November
    
    // Helper to format date as YYYY-MM-DD in local timezone
    const formatLocalDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    // Calculate number of weeks from target date to current Sunday
    const msPerWeek = 7 * 24 * 60 * 60 * 1000;
    const weeksDiff = Math.ceil((currentSunday.getTime() - targetDate.getTime()) / msPerWeek);
    const numWeeks = Math.max(weeksDiff + 1, 12); // At least 12 weeks, or more to reach target date
    
    // Generate weeks (current + past weeks back to Nov 23, 2025)
    for (let i = 0; i < numWeeks; i++) {
      const weekStart = new Date(currentSunday);
      weekStart.setDate(currentSunday.getDate() - (i * 7));
      
      // Stop if we've gone past the target date
      if (weekStart < targetDate) break;
      
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      
      weeks.push({
        start: formatLocalDate(weekStart),
        end: formatLocalDate(weekEnd),
        label: `${formatDateShort(formatLocalDate(weekStart))} - ${formatDateShort(formatLocalDate(weekEnd))}${i === 0 ? ' (Current Week)' : ''}`
      });
    }
    return weeks;
  };

  const pastWeeks = getPastWeeks();

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const fetchPromises = [
        jobsAPI.getActive(),
        resourcesAPI.getActive(),
        clientsAPI.getAll(),
        timesheetsAPI.getCurrentPeriod()
      ];
      
      // Admin needs to fetch subcontractors list for creating timesheets on behalf of users
      if (isAdmin && isNew) {
        fetchPromises.push(usersAPI.getSubcontractors());
      }
      
      const results = await Promise.all(fetchPromises);
      const [jobsRes, resourcesRes, clientsRes, periodRes] = results;

      setAllJobs(jobsRes.data);
      setResources(resourcesRes.data);
      setClients(clientsRes.data);
      setCurrentPeriod(periodRes.data);
      
      // Set subcontractors list for admin (sorted alphabetically)
      if (isAdmin && isNew && results[4]) {
        const sortedSubs = results[4].data.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setSubcontractors(sortedSubs);
      }

      if (!isNew) {
        const tsRes = await timesheetsAPI.getById(id);
        setTimesheet(tsRes.data);
        setSelectedJob(tsRes.data.job_id);
        setEntries(tsRes.data.entries || []);
        setComments(tsRes.data.comments || '');
        setAttachments(tsRes.data.attachments || []);
        
        // For existing timesheets, show all jobs (already selected job should be visible)
        setJobs(jobsRes.data);
        
        // Fetch pay rate for this job to determine unit type display
        const tsUserResource = resourcesRes.data.find(r => r.user_id === tsRes.data.user_id);
        if (tsUserResource && tsRes.data.job_id) {
          try {
            const payRateRes = await resourcesAPI.getPayRateForJob(tsUserResource.id, tsRes.data.job_id);
            setCurrentPayRate(payRateRes.data);
          } catch (prErr) {
            console.error('Failed to fetch pay rate:', prErr);
          }
        }
        
        // Fetch the timesheet owner's information for admins
        if (tsRes.data.user_id) {
          try {
            const ownerRes = await usersAPI.getById(tsRes.data.user_id);
            setTimesheetOwner(ownerRes.data);
          } catch (ownerErr) {
            console.error('Failed to fetch timesheet owner:', ownerErr);
          }
        }
        
        // Fetch linked invoices
        try {
          const [clientInvRes, subInvRes] = await Promise.all([
            clientInvoicesAPI.getAll(),
            subcontractorInvoicesAPI.getAll()
          ]);
          
          const clientLinked = clientInvRes.data.filter(inv => 
            inv.timesheet_ids && inv.timesheet_ids.includes(id)
          );
          const subLinked = subInvRes.data.filter(inv => 
            inv.timesheet_ids && inv.timesheet_ids.includes(id)
          );
          
          setLinkedInvoices({ client: clientLinked, subcontractor: subLinked });
        } catch (invErr) {
          console.error('Failed to fetch linked invoices:', invErr);
        }
      } else {
        // Initialize with empty entry for current week
        // For admin, resource will be set based on selected user
        const defaultUserId = isAdmin ? '' : user?.id;
        const userResource = resourcesRes.data.find(r => r.user_id === defaultUserId);
        setSelectedWeekStart(periodRes.data.start_date);
        setSelectedUserId(defaultUserId);
        
        // Reset comments and attachments for new timesheets
        setComments('');
        setAttachments([]);
        
        // Calculate Monday's date (start_date is Sunday, so add 1 day)
        const sundayDate = new Date(periodRes.data.start_date + 'T12:00:00');
        const mondayDate = new Date(sundayDate);
        mondayDate.setDate(sundayDate.getDate() + 1);
        const mondayDateStr = mondayDate.toISOString().split('T')[0];
        
        // For non-admin users, determine unit type based on their pay rate
        let defaultUnitType = 'days';
        if (!isAdmin && userResource) {
          try {
            const payRateRes = await resourcesAPI.getPayRateForJob(userResource.id, 'default');
            const payRate = payRateRes.data;
            if (payRate) {
              const hasDailyRate = payRate.daily_rate > 0;
              const hasHourlyRate = payRate.hourly_rate > 0;
              if (!hasDailyRate && hasHourlyRate) {
                defaultUnitType = 'hours';
              }
              setCurrentPayRate(payRate);
            }
          } catch (err) {
            console.error('Failed to fetch default pay rate:', err);
          }
        }
        
        setEntries([{
          id: Date.now().toString(),
          date: mondayDateStr,
          description: '',
          resource_id: userResource?.id || '',
          unit_type: defaultUnitType,
          quantity: 0,
          accommodation_amount: 0,
          kms_travelled: 0,
          one_way_trip: false
        }]);
        
        // For new timesheets, fetch available jobs for the user
        if (!isAdmin && user?.id) {
          await loadAvailableJobs(user.id, jobsRes.data);
        } else if (isAdmin) {
          // Admin needs to wait for user selection
          setJobs([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setError('Failed to load timesheet data');
    } finally {
      setLoading(false);
    }
  };
  
  // Load available jobs based on user's pay rates
  const loadAvailableJobs = async (userId, allJobsList) => {
    try {
      const availableRes = await usersAPI.getAvailableJobs(userId);
      const availableIds = availableRes.data;
      setAvailableJobIds(availableIds);
      
      if (availableIds.length > 0) {
        // Filter jobs to only those the user has pay rates for
        const filteredJobs = allJobsList.filter(job => availableIds.includes(job.id));
        setJobs(filteredJobs);
      } else {
        // No pay rates set up - show message (empty jobs list)
        setJobs([]);
      }
    } catch (err) {
      console.error('Failed to load available jobs:', err);
      // Fallback to showing all jobs if there's an error
      setJobs(allJobsList);
    }
  };

  const addEntry = () => {
    // Use selectedUserId for admin creating on behalf, otherwise current user
    const targetUserId = isNew && isAdmin && selectedUserId ? selectedUserId : user?.id;
    const userResource = resources.find(r => r.user_id === targetUserId);
    const weekStart = isNew ? selectedWeekStart : (timesheet?.period_start || currentPeriod?.start_date);
    
    // Get all dates already used by existing entries
    const usedDates = new Set(entries.map(e => e.date));
    
    // Generate all week dates in order (Sunday to Saturday)
    const getWeekDatesArray = () => {
      if (!weekStart) return [];
      const startDate = new Date(weekStart + 'T12:00:00');
      const dates = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        dates.push(date.toISOString().split('T')[0]);
      }
      return dates;
    };
    
    const allWeekDates = getWeekDatesArray();
    
    // Find the next sequential date after the last entry
    let nextDate = '';
    
    if (entries.length === 0) {
      // No entries yet - start with Monday (second day of the week, index 1)
      nextDate = allWeekDates[1] || allWeekDates[0] || weekStart || '';
    } else {
      // Find the latest date used in entries
      const entryDates = entries.map(e => e.date).filter(d => d);
      const sortedEntryDates = entryDates.sort();
      const lastDate = sortedEntryDates[sortedEntryDates.length - 1];
      
      // Find the index of the last date in the week
      const lastDateIndex = allWeekDates.indexOf(lastDate);
      
      if (lastDateIndex >= 0 && lastDateIndex < 6) {
        // Try the next day in sequence
        const nextDayIndex = lastDateIndex + 1;
        const nextDayDate = allWeekDates[nextDayIndex];
        
        if (!usedDates.has(nextDayDate)) {
          nextDate = nextDayDate;
        } else {
          // Next sequential day is used, find the next available day after that
          for (let i = nextDayIndex + 1; i < 7; i++) {
            if (!usedDates.has(allWeekDates[i])) {
              nextDate = allWeekDates[i];
              break;
            }
          }
        }
      }
      
      // If still no date found, look for any unused date from the beginning
      if (!nextDate) {
        nextDate = allWeekDates.find(d => !usedDates.has(d)) || '';
      }
    }
    
    // If all days are used, show an error and don't add a new entry
    if (!nextDate && entries.length >= 7) {
      toast.error('All days of the week already have entries. You cannot add more entries for the same project in this period.');
      return;
    }
    
    // If no unused date found but entries < 7, fallback to weekStart (edge case)
    if (!nextDate) {
      nextDate = weekStart || '';
    }
    
    // Determine default unit type based on pay rate
    let defaultUnitType = 'days';
    if (currentPayRate) {
      const hasDailyRate = currentPayRate.daily_rate > 0;
      const hasHourlyRate = currentPayRate.hourly_rate > 0;
      if (!hasDailyRate && hasHourlyRate) {
        defaultUnitType = 'hours';
      }
    }
    
    setEntries([...entries, {
      id: Date.now().toString(),
      date: nextDate,
      description: '',
      resource_id: userResource?.id || '',
      unit_type: defaultUnitType,
      quantity: 0,
      accommodation_amount: 0,
      kms_travelled: 0,
      one_way_trip: false
    }]);
  };
  
  // Update resource_id in entries when selected user changes (admin only)
  const handleUserChange = async (newUserId) => {
    setSelectedUserId(newUserId);
    setSelectedJob(''); // Reset job selection when user changes
    const userResource = resources.find(r => r.user_id === newUserId);
    
    // Fetch the user's default pay rate to pre-populate unit type
    let defaultPayRate = null;
    if (userResource) {
      try {
        const payRateRes = await resourcesAPI.getPayRateForJob(userResource.id, 'default');
        defaultPayRate = payRateRes.data;
        setCurrentPayRate(defaultPayRate);
      } catch (err) {
        console.error('Failed to fetch default pay rate:', err);
        setCurrentPayRate(null);
      }
    } else {
      setCurrentPayRate(null);
    }
    
    // Determine default unit type based on default pay rate
    let defaultUnitType = 'days';
    if (defaultPayRate) {
      const hasDailyRate = defaultPayRate.daily_rate > 0;
      const hasHourlyRate = defaultPayRate.hourly_rate > 0;
      if (!hasDailyRate && hasHourlyRate) {
        defaultUnitType = 'hours';
      }
    }
    
    // Update entries with resource_id and unit_type
    setEntries(prevEntries => prevEntries.map(e => ({
      ...e,
      resource_id: userResource?.id || '',
      unit_type: defaultUnitType
    })));
    
    // Load available jobs for the selected user
    if (newUserId) {
      await loadAvailableJobs(newUserId, allJobs);
    } else {
      setJobs([]);
    }
  };

  // Fetch pay rate when job changes to determine available unit types
  const handleJobChange = async (jobId) => {
    setSelectedJob(jobId);
    
    // Get the resource for the current user
    const targetUserId = isNew && isAdmin && selectedUserId ? selectedUserId : user?.id;
    const userResource = resources.find(r => r.user_id === targetUserId);
    
    if (userResource && jobId) {
      try {
        const payRateRes = await resourcesAPI.getPayRateForJob(userResource.id, jobId);
        const payRate = payRateRes.data;
        setCurrentPayRate(payRate);
        
        // Auto-set unit_type for new entries based on available rates
        const hasDailyRate = payRate && payRate.daily_rate > 0;
        const hasHourlyRate = payRate && payRate.hourly_rate > 0;
        
        // Determine default unit type
        let defaultUnitType = 'days';
        if (!hasDailyRate && hasHourlyRate) {
          defaultUnitType = 'hours';
        } else if (hasDailyRate && !hasHourlyRate) {
          defaultUnitType = 'days';
        }
        // If both or neither, keep 'days' as default
        
        // Update existing entries to use the appropriate unit type
        setEntries(prevEntries => prevEntries.map(e => ({
          ...e,
          unit_type: defaultUnitType
        })));
      } catch (err) {
        console.error('Failed to fetch pay rate:', err);
        setCurrentPayRate(null);
      }
    } else {
      setCurrentPayRate(null);
    }
  };

  // Determine what unit type options to show
  const getUnitTypeOptions = () => {
    if (!currentPayRate) {
      // No pay rate info - show both options
      return { hasDays: true, hasHours: true, showDropdown: true };
    }
    
    const hasDailyRate = currentPayRate.daily_rate > 0;
    const hasHourlyRate = currentPayRate.hourly_rate > 0;
    
    return {
      hasDays: hasDailyRate,
      hasHours: hasHourlyRate,
      showDropdown: hasDailyRate && hasHourlyRate // Only show dropdown if both are available
    };
  };

  const unitTypeOptions = getUnitTypeOptions();

  const removeEntry = (entryId) => {
    setEntries(entries.filter(e => e.id !== entryId));
  };

  const updateEntry = (entryId, field, value) => {
    // If changing the date, check for duplicate dates
    if (field === 'date') {
      const otherEntriesWithSameDate = entries.filter(e => 
        e.id !== entryId && e.date === value
      );
      if (otherEntriesWithSameDate.length > 0) {
        toast.error('An entry for this date already exists. Each day can only have one entry per project.');
        return;
      }
    }
    setEntries(entries.map(e => 
      e.id === entryId ? { ...e, [field]: value } : e
    ));
  };

  const calculateTotals = () => {
    let totalHours = 0;
    let totalDays = 0;
    entries.forEach(e => {
      if (e.unit_type === 'hours') {
        totalHours += parseFloat(e.quantity) || 0;
      } else {
        totalDays += parseFloat(e.quantity) || 0;
      }
    });
    return { totalHours, totalDays };
  };

  const handleSave = async () => {
    if (!selectedJob) {
      setError('Please select a project');
      return;
    }
    
    // Admin must select a user when creating on behalf
    if (isNew && isAdmin && !selectedUserId) {
      setError('Please select a subcontractor');
      return;
    }

    // For new timesheets, use selected week; otherwise use existing period
    const periodStart = isNew ? selectedWeekStart : currentPeriod.start_date;
    const selectedWeek = pastWeeks.find(w => w.start === periodStart);
    const periodEnd = selectedWeek ? selectedWeek.end : currentPeriod.end_date;

    setSaving(true);
    setError('');

    try {
      // Use selectedUserId for admin creating on behalf, otherwise current user
      const targetUserId = isNew && isAdmin && selectedUserId ? selectedUserId : user.id;
      
      const data = {
        job_id: selectedJob,
        user_id: targetUserId,
        period_start: periodStart,
        period_end: periodEnd,
        comments: comments,
        entries: entries.map(({ id, ...e }) => ({
          ...e,
          quantity: parseFloat(e.quantity) || 0,
          accommodation_amount: parseFloat(e.accommodation_amount) || 0,
          kms_travelled: parseFloat(e.kms_travelled) || 0,
          one_way_trip: e.one_way_trip || false
        }))
      };

      if (isNew) {
        const response = await timesheetsAPI.create(data);
        const newTimesheetId = response.data.id;
        
        // Upload any pending attachments
        if (attachmentsRef.current) {
          const pendingFiles = attachmentsRef.current.getPendingFiles?.() || [];
          for (const file of pendingFiles) {
            try {
              await timesheetsAPI.uploadAttachment(newTimesheetId, file);
            } catch (uploadError) {
              console.error('Failed to upload attachment:', uploadError);
            }
          }
        }
        
        navigate(`/timesheets/${newTimesheetId}`);
      } else {
        await timesheetsAPI.update(id, { 
          job_id: selectedJob,
          comments: comments,
          entries: data.entries 
        });
        fetchData();
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to save timesheet');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');

    try {
      // Save first if there are changes
      if (isNew || timesheet?.status === 'draft' || timesheet?.status === 'rejected') {
        await handleSave();
      }
      await timesheetsAPI.submit(timesheet?.id || id);
      fetchData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to submit timesheet');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    setSaving(true);
    try {
      await timesheetsAPI.approve(id);
      toast.success('Timesheet approved');
      navigate('/approvals');
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to approve timesheet');
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectComment.trim()) {
      setError('Please provide a rejection reason');
      return;
    }

    setSaving(true);
    try {
      await timesheetsAPI.reject(id, rejectComment);
      setShowRejectModal(false);
      toast.success('Timesheet rejected');
      navigate('/approvals');
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to reject timesheet');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this timesheet?')) return;

    try {
      await timesheetsAPI.delete(id);
      navigate('/timesheets');
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to delete timesheet');
    }
  };

  const handleDownloadPDF = async () => {
    if (!timesheet?.id) return;
    
    setDownloading(true);
    try {
      const response = await reportsAPI.downloadTimesheetPDF(timesheet.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `timesheet_${timesheet.period_start}_${timesheet.period_end}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setError('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#194eb2]" />
        </div>
      </DashboardLayout>
    );
  }

  const { totalHours, totalDays } = calculateTotals();
  // Admins can edit any timesheet regardless of status
  // Regular users can only edit draft or rejected timesheets
  const canEdit = isNew || isAdmin || timesheet?.status === 'draft' || timesheet?.status === 'rejected';
  const canSubmit = !isNew && (timesheet?.status === 'draft' || timesheet?.status === 'rejected');
  const canApprove = isAdmin && timesheet?.status === 'submitted';

  const selectedJobDetails = jobs.find(j => j.id === selectedJob);
  const selectedClient = clients.find(c => c.id === selectedJobDetails?.client_id);

  // Generate dates for the week
  const getWeekDates = () => {
    const weekStart = isNew ? selectedWeekStart : (timesheet?.period_start || currentPeriod?.start_date);
    if (!weekStart) return [];
    
    const startDate = new Date(weekStart);
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const weekDates = getWeekDates();
  
  // Handler for week selection change
  const handleWeekChange = (newWeekStart) => {
    setSelectedWeekStart(newWeekStart);
    
    // Calculate Monday's date (newWeekStart is Sunday, so add 1 day)
    const sundayDate = new Date(newWeekStart + 'T12:00:00');
    const mondayDate = new Date(sundayDate);
    mondayDate.setDate(sundayDate.getDate() + 1);
    const mondayDateStr = mondayDate.toISOString().split('T')[0];
    
    // Update entry dates to start from Monday of new week
    setEntries(entries.map((e, index) => {
      // Calculate the date for each entry based on its position (starting from Monday)
      const entryDate = new Date(sundayDate);
      entryDate.setDate(sundayDate.getDate() + 1 + index); // +1 for Monday, +index for subsequent days
      return {
        ...e,
        date: entryDate.toISOString().split('T')[0]
      };
    }));
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/timesheets" className="p-2 hover:bg-slate-100 rounded-md">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                {isNew ? 'New Timesheet' : (
                  timesheetOwner ? `Timesheet for ${timesheetOwner.name}` : 'Timesheet'
                )}
              </h1>
              <p className="text-sm text-slate-500">
                {isNew 
                  ? (selectedWeekStart ? formatDateRange(selectedWeekStart, pastWeeks.find(w => w.start === selectedWeekStart)?.end) : 'Select a week')
                  : formatDateRange(timesheet?.period_start, timesheet?.period_end)
                }
              </p>
            </div>
            {timesheet && <StatusBadge status={timesheet.status} />}
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            {canEdit && (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn-secondary flex items-center gap-2"
                  data-testid="save-timesheet"
                >
                  <Save className="w-4 h-4" />
                  Save Draft
                </button>
                {!isNew && (
                  <button
                    onClick={handleSubmit}
                    disabled={saving}
                    className="btn-primary flex items-center gap-2"
                    data-testid="submit-timesheet"
                  >
                    <Send className="w-4 h-4" />
                    Submit
                  </button>
                )}
              </>
            )}
            {canApprove && (
              <>
                <button
                  onClick={() => setShowRejectModal(true)}
                  disabled={saving}
                  className="btn-secondary text-red-600 border-red-200 hover:bg-red-50 flex items-center gap-2"
                  data-testid="reject-timesheet"
                >
                  <X className="w-4 h-4" />
                  Reject
                </button>
                <button
                  onClick={handleApprove}
                  disabled={saving}
                  className="btn-primary bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2"
                  data-testid="approve-timesheet"
                >
                  <Check className="w-4 h-4" />
                  Approve
                </button>
              </>
            )}
            {/* Download PDF button - show for non-new timesheets */}
            {!isNew && timesheet && (
              <button
                onClick={handleDownloadPDF}
                disabled={downloading}
                className="btn-secondary flex items-center gap-2"
                data-testid="download-pdf"
              >
                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Download PDF
              </button>
            )}
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Rejection comment */}
        {timesheet?.status === 'rejected' && timesheet?.rejection_comment && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm font-medium text-red-700">Rejection Reason:</p>
            <p className="text-sm text-red-600 mt-1">{timesheet.rejection_comment}</p>
          </div>
        )}

        {/* Linked Invoices */}
        {!isNew && (linkedInvoices.client.length > 0 || linkedInvoices.subcontractor.length > 0) && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm font-medium text-blue-800 mb-2">Linked Invoices:</p>
            <div className="flex flex-wrap gap-2">
              {linkedInvoices.client.map(inv => (
                <Link
                  key={inv.id}
                  to="/invoices"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-blue-300 rounded-full text-sm font-medium text-blue-700 hover:bg-blue-100 transition-colors"
                >
                  <CreditCard className="w-3.5 h-3.5" />
                  Client: {inv.invoice_number}
                  <span className={`ml-1 px-1.5 py-0.5 text-xs rounded ${
                    inv.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {inv.status === 'paid' ? 'Paid' : 'Pending'}
                  </span>
                </Link>
              ))}
              {linkedInvoices.subcontractor.map(inv => (
                <Link
                  key={inv.id}
                  to="/payments"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-amber-300 rounded-full text-sm font-medium text-amber-700 hover:bg-amber-100 transition-colors"
                >
                  <DollarSign className="w-3.5 h-3.5" />
                  Sub: {inv.invoice_number}
                  <span className={`ml-1 px-1.5 py-0.5 text-xs rounded ${
                    inv.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {inv.status === 'paid' ? 'Paid' : 'Pending'}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Header Info */}
        <div className="card-base">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-slate-900">Timesheet Details</h2>
          </div>
          <div className="card-body grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Admin can select subcontractor when creating new timesheet */}
            {isNew && isAdmin && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Subcontractor <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedUserId}
                  onChange={(e) => handleUserChange(e.target.value)}
                  className="input-base"
                  data-testid="user-select"
                >
                  <option value="">Select subcontractor...</option>
                  {[...subcontractors].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((sub) => (
                    <option key={sub.id} value={sub.id}>
                      {sub.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Project</label>
              <select
                value={selectedJob}
                onChange={(e) => handleJobChange(e.target.value)}
                disabled={!canEdit || (isNew && jobs.length === 0)}
                className="input-base"
                data-testid="job-select"
              >
                <option value="">Select a project...</option>
                {jobs.map((job) => (
                  <option key={job.id} value={job.id}>
                    {job.job_number} - {job.name}
                  </option>
                ))}
              </select>
              {isNew && jobs.length === 0 && (isAdmin ? selectedUserId : true) && (
                <p className="mt-1 text-xs text-amber-600">
                  {isAdmin && selectedUserId 
                    ? "No jobs configured for this user. Set up pay rates in Users → Manage Rates."
                    : !isAdmin 
                      ? "No projects available. Contact admin to set up your pay rates."
                      : "Select a subcontractor first."
                  }
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Client</label>
              <input
                type="text"
                value={selectedClient?.name || '-'}
                disabled
                className="input-base bg-slate-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Weekly Period</label>
              {isNew ? (
                <select
                  value={selectedWeekStart || ''}
                  onChange={(e) => handleWeekChange(e.target.value)}
                  className="input-base"
                  data-testid="week-select"
                >
                  {pastWeeks.map((week) => (
                    <option key={week.start} value={week.start}>
                      {week.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={formatDateRange(timesheet?.period_start, timesheet?.period_end)}
                  disabled
                  className="input-base bg-slate-50"
                />
              )}
            </div>
          </div>
        </div>

        {/* Entries Table */}
        <div className="card-base">
          <div className="card-header flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">Time Entries</h2>
            {canEdit && (
              <button
                onClick={addEntry}
                className="btn-secondary flex items-center gap-2"
                data-testid="add-entry"
              >
                <Plus className="w-4 h-4" />
                Add Entry
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="table-header">
                <tr>
                  <th className="px-3 py-3 text-left">Date</th>
                  <th className="px-3 py-3 text-left">Description</th>
                  <th className="px-3 py-3 text-left">Unit</th>
                  <th className="px-3 py-3 text-left">Qty</th>
                  <th className="px-3 py-3 text-left">Accom/Disb</th>
                  <th className="px-3 py-3 text-left">Km's</th>
                  <th className="px-3 py-3 text-left">Return Trip</th>
                  {canEdit && <th className="px-3 py-3"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {entries.length === 0 ? (
                  <tr>
                    <td colSpan={canEdit ? 8 : 7} className="px-4 py-8 text-center text-slate-500">
                      No entries yet. Click "Add Entry" to start.
                    </td>
                  </tr>
                ) : (
                  entries.map((entry, index) => {
                    // Get dates used by other entries (not this one)
                    const usedByOthers = new Set(entries.filter(e => e.id !== entry.id).map(e => e.date));
                    
                    return (
                    <tr key={entry.id} className="table-row">
                      <td className="px-3 py-3">
                        <select
                          value={entry.date}
                          onChange={(e) => updateEntry(entry.id, 'date', e.target.value)}
                          disabled={!canEdit}
                          className="input-base w-32 text-sm"
                        >
                          {weekDates.map((date) => (
                            <option 
                              key={date} 
                              value={date}
                              disabled={usedByOthers.has(date)}
                            >
                              {formatDateShort(date)}{usedByOthers.has(date) ? ' (used)' : ''}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-3">
                        <input
                          type="text"
                          value={entry.description}
                          onChange={(e) => updateEntry(entry.id, 'description', e.target.value)}
                          disabled={!canEdit}
                          placeholder="Enter description..."
                          className="input-base min-w-48"
                          data-testid={`entry-description-${index}`}
                        />
                      </td>
                      <td className="px-3 py-3">
                        {unitTypeOptions.showDropdown ? (
                          <select
                            value={entry.unit_type}
                            onChange={(e) => updateEntry(entry.id, 'unit_type', e.target.value)}
                            disabled={!canEdit}
                            className="input-base w-24 text-sm"
                          >
                            {unitTypeOptions.hasDays && <option value="days">Days</option>}
                            {unitTypeOptions.hasHours && <option value="hours">Hours</option>}
                          </select>
                        ) : (
                          <span className="text-sm text-slate-600 font-medium px-2">
                            {entry.unit_type === 'hours' ? 'Hours' : 'Days'}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <input
                          type="number"
                          value={entry.quantity}
                          onChange={(e) => updateEntry(entry.id, 'quantity', e.target.value)}
                          disabled={!canEdit}
                          step="0.5"
                          min="0"
                          className="input-base w-20 font-mono text-sm"
                          data-testid={`entry-quantity-${index}`}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <select
                          value={entry.accommodation_amount ? '1' : '0'}
                          onChange={(e) => updateEntry(entry.id, 'accommodation_amount', e.target.value === '1' ? 1 : 0)}
                          disabled={!canEdit}
                          className="input-base w-16 text-sm"
                          data-testid={`entry-accommodation-${index}`}
                        >
                          <option value="0">0</option>
                          <option value="1">1</option>
                        </select>
                      </td>
                      <td className="px-3 py-3">
                        <input
                          type="number"
                          value={entry.kms_travelled || 0}
                          onChange={(e) => updateEntry(entry.id, 'kms_travelled', e.target.value)}
                          disabled={!canEdit}
                          step="1"
                          min="0"
                          className="input-base w-20 font-mono text-sm"
                          data-testid={`entry-kms-${index}`}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <select
                          value={entry.one_way_trip ? '1' : '0'}
                          onChange={(e) => updateEntry(entry.id, 'one_way_trip', e.target.value === '1')}
                          disabled={!canEdit}
                          className="input-base w-16 text-sm"
                          data-testid={`entry-return-${index}`}
                        >
                          <option value="0">0</option>
                          <option value="1">1</option>
                        </select>
                      </td>
                      {canEdit && (
                        <td className="px-3 py-3">
                          <button
                            onClick={() => removeEntry(entry.id)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-md"
                            data-testid={`remove-entry-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  )})
                )}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
            <div className="flex justify-end gap-8">
              {totalDays > 0 && (
                <div className="text-right">
                  <p className="text-sm text-slate-500">Total Days</p>
                  <p className="text-2xl font-bold text-slate-900 font-mono">{totalDays.toFixed(1)}</p>
                </div>
              )}
              {totalHours > 0 && (
                <div className="text-right">
                  <p className="text-sm text-slate-500">Total Hours</p>
                  <p className="text-2xl font-bold text-slate-900 font-mono">{totalHours.toFixed(1)}</p>
                </div>
              )}
              {totalDays === 0 && totalHours === 0 && (
                <div className="text-right">
                  <p className="text-sm text-slate-500">No entries</p>
                  <p className="text-lg text-slate-400">Add entries above</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Weekly Comments */}
        <div className="card-base">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-slate-900">Weekly Comments</h2>
          </div>
          <div className="card-body">
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              disabled={!canEdit}
              placeholder="Add any comments for this week's timesheet (optional)..."
              rows={3}
              className="input-base w-full resize-none"
              data-testid="timesheet-comments"
            />
          </div>
        </div>

        {/* Attachments */}
        <div className="card-base">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Paperclip className="w-5 h-5" />
              Attachments
            </h2>
          </div>
          <div className="card-body">
            <TimesheetAttachments
              ref={attachmentsRef}
              timesheetId={id}
              attachments={attachments}
              onAttachmentsChange={setAttachments}
              readOnly={!canEdit && !isAdmin}
              isNew={isNew}
            />
          </div>
        </div>

        {/* Delete button - admins can delete any, users only drafts */}
        {!isNew && (isAdmin || timesheet?.status === 'draft') && (
          <div className="flex justify-start mb-16">
            <button
              onClick={handleDelete}
              className="text-sm text-red-600 hover:underline flex items-center gap-1"
              data-testid="delete-timesheet"
            >
              <Trash2 className="w-4 h-4" />
              Delete this timesheet
              {isAdmin && timesheet?.status !== 'draft' && (
                <span className="text-xs text-red-400 ml-1">(Admin)</span>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowRejectModal(false)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Reject Timesheet</h3>
            <textarea
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              placeholder="Please provide a reason for rejection..."
              className="input-base h-32 resize-none"
              data-testid="reject-comment"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowRejectModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={saving}
                className="btn-primary bg-red-600 hover:bg-red-700"
                data-testid="confirm-reject"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default TimesheetDetailPage;
